import React, { useState, useEffect, useRef } from 'react';
import getCountryCodes from '../../translations/countryCodes';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import omit from 'lodash/omit';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import ElbwalkerInitializer from '../../analytics/elbwalkerInitializer.js';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  stringify,
  parse,
} from '../../util/urlHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { translateRoomType } from '../../util/roomTypeHelpers';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import ActionBarMaybe from './ActionBarMaybe';

import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { redirectLegacyPath } from '../../util/legacyRedirect';

/**Newly migrated components*/
import ListingHeader from './ListingPageComponents/ListingHeader/ListingHeader';
import { getListingReviewStats } from './ListingPageComponents/reviewStats';
import ImageGallery from './ListingPageComponents/ImageGallery/ImageGallery';
import { ImpactTagsRow, ImpactStrip, HostTeaser } from './ListingPageComponents/ImpactBar/ImpactBar';
import BookingPanel from './ListingPageComponents/BookingPanel/BookingPanel';
import Highlights from './ListingPageComponents/Highlights/Highlights';
import DescriptionSection from './ListingPageComponents/DescriptionSection/DescriptionSection';
import VideoSection from './ListingPageComponents/VideoSection/VideoSection';
import ReviewsSection from './ListingPageComponents/ReviewsSection/ReviewsSection';
import AboutHost from './ListingPageComponents/AboutHost/AboutHost';
import ContactHelp from './ListingPageComponents/ContactHelp/ContactHelp';
import GutZuWissen from './ListingPageComponents/GutZuWissen/GutZuWissen';

import OtherListings from './ListingPageComponents/OtherListings/OtherListings';
import MapSection from './ListingPageComponents/MapSection/MapSection';
import OnSiteTabs from './ListingPageComponents/OnSiteTabs/OnSiteTabs';
import FAQAccordion from './ListingPageComponents/FAQAccordion/FAQAccordion';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  speculateTransactionLP,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmit,
} from './ListingPage.shared';

import css from './ListingPage.module.css';
import AuthModal from '../../components/AuthModal/AuthModal';



import DiscoverListings from './ListingPageComponents/ListingSuggestions/discoverListingSuggestions';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { ensureCurrentUser } from '../../util/data';
import Notification from '../../components/notification-popup/notification.js';

import { extractDaysAndNights } from '../../util/listingPages.util.js';
import Footer from '../../components/Footer/FooterNew.jsx';
import BulletPointsFormatter from '../../util/formatters/bulletPointsFormatter/bulletPointsFormatter.jsx';
// Optimized Material UI import for smaller bundle size
import ForumRounded from '@material-ui/icons/ForumRounded';
import useListingContactModal from './useListingContactModal';
import ListingContactModal from './ListingContactModal';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [showModalFromOutside, setShowModalFromOutside] = useState(false);
  const [orderValues, setOrderValues] = useState('');
  const [favorite, Setfavorite] = useState();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [callFav, setCallFav] = useState(false);
  const [notification, setNotification] = useState(false);

  const {
    isModalOpen: isContactModalOpen,
    handleClose: handleContactModalClose,
  } = useListingContactModal();

  const openModalFromOutside = value => {
    setShowModalFromOutside(value);
  };

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    fetchSpeculatedTransaction,
    listingConfig: listingConfigProp,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    config,
    routeConfiguration,
    listings,
  } = props;

  const { lang } = useParams();

  useEffect(() => {
    if (!rawParams.id) return;

    document.body.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    });
  }, [rawParams.id]);

  // prop override makes testing a bit easier
  // TODO: improve this when updating test setup
  // const listingConfig = listingConfigProp || config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const isExperienceType =
    currentListing.attributes?.publicData?.listingType == 'impact-experience';
  // Walker binds `data-elb-<entity>` properties by entity name, so the property
  // attributes below have to be keyed off the same value as `data-elb`.
  const elbEntity = isExperienceType ? 'exp' : 'accm';
  const elbProps = value => ({ [`data-elb-${elbEntity}`]: value });
  const countryCodes = getCountryCodes(config.locale);
  const user = ensureCurrentUser(currentUser);
  let isFavArray = user.attributes.profile?.publicData?.favoritList ?? [];
  useEffect(() => {
    let isFav = isFavArray.findIndex(item => item.listId === listingId.uuid);
    if (isFav !== -1) {
      Setfavorite(true);
    }
  }, [user, isFavArray]);
  const { onUpdateProfile } = props;
  const favoriteFunction = async () => {
    if (!user.attributes.profile.publicData.favoritList) {
      user.attributes.profile.publicData.favoritList = [];
    }
    const favoritList = user.attributes.profile.publicData.favoritList;
    const index = favoritList.findIndex(item => item.listId === listingId.uuid);
    if (index !== -1) {
      favoritList.splice(index, 1);
      Setfavorite(false);
      await onUpdateProfile({ publicData: { favoritList } }).then(resp => {
        // Store the translation id (not the formatted string) so the notification
        // re-translates when the language is switched while it is still visible.
        setNotification('favoritePage.removed');
      });
    } else {
      favoritList.push({ listId: listingId.uuid });
      Setfavorite(true);
      await onUpdateProfile({ publicData: { favoritList } }).then(resp => {
        setNotification('favoritePage.added');
      });
    }
  };
  //get country name and set it as a state
  const [countrycodeLang, setCountrycodeLang] = useState(' ');
  const language = intl.locale.split('-')[0];
  const countriesInLanguage = getCountryCodes(language);
  const [countrynameNew, setCountrynameNew] = useState('');
  const [showPriceDetails, setShowPriceDetails] = useState(false);
  const [bookingDisabled, setBookingDisabled] = useState(false);
  const [showBookingNoAvailableError, setShowBookingNoAvailableError] = useState(false);

  useEffect(() => {
    const countryCodeTemp =
      currentListing && currentListing.attributes.publicData.country
        ? currentListing.attributes.publicData.country
        : [];
    const newCountryName = getCountryName(countryCodeTemp);
    setCountrycodeLang(countryCodeTemp);
    const codeAndCountry = countriesInLanguage.find(country => country.code === countrycodeLang);
    if (codeAndCountry) {
      setCountrynameNew(codeAndCountry.name);
    }
  }, [currentListing, countriesInLanguage, countrycodeLang]);

  function getCountryName(countryCode) {
    const country = countryCodes.find(country => country.code === countryCode);
    return country ? country.name : 'Country code not found';
  }

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;
  const listingReviewStats = getListingReviewStats(currentListing, reviews);
  const currentListingRating = listingReviewStats.rating;

  let listingsItems = [];
  if (props.listings.length > 0) {
    props.listings.map(listing => {
      listingsItems.push(listing);
    });
  }
  const GetRoomTypesCard = () => (
    <OtherListings listingsItems={listingsItems} />
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const topbar = <TopbarContainer isListingPage={true} />;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  if (showListingError && showListingError.status === 404) {
    const legacyRedirect = redirectLegacyPath(location);
    if (legacyRedirect) {
      return legacyRedirect;
    }
    return <NotFoundPage />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );
  //from here until line 247  is only for translate
  const newTitle = title.split('• ');
  const roomtypeTranslation = isExperienceType
    ? null
    : translateRoomType(newTitle[1], intl);

  //is experience
  const experienceType = newTitle[1];
  const experienceTitle =
    experienceType === '2 Days 1 Night'
      ? intl.formatMessage({ id: 'experienceTitle.2d1n' })
      : experienceType === '3 Days 2 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.3d2n' })
      : experienceType === '4 Days 3 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.4d3n' })
      : experienceType === '5 Days 4 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.5d4n' })
      : experienceType === '6 Days 5 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.6d5n' })
      : experienceType === '7 Days 6 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.1w' })
      : experienceType === '8 Days 7 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.8d7n' })
      : experienceType === '9 Days 8 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.9d8n' })
      : experienceType === '10 Days 9 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.10d9n' })
      : experienceType === '11 Days 10 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.11d10n' })
      : experienceType === '12 Days 11 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.12d11n' })
      : experienceType === '13 Days 12 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.13d12n' })
      : experienceType === '14 Days 13 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.2w' })
      : experienceType === '15 Days 14 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.15d14n' })
      : experienceType === '16 Days 15 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.16d15n' })
      : experienceType === '17 Days 16 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.17d16n' })
      : experienceType === '18 Days 17 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.18d17n' })
      : experienceType === '19 Days 18 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.19d18n' })
      : experienceType === '20 Days 19 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.20d19n' })
      : experienceType === '21 Days 20 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.3w' })
      : experienceType === '28 Days 27 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.4w' })
      : experienceType === '42 Days 41 Nights'
      ? intl.formatMessage({ id: 'experienceTitle.42d41n' })
      : null;

  const titleSecondPart = isExperienceType ? experienceTitle : roomtypeTranslation;
  const newTitle2 = newTitle[0] + '• ' + titleSecondPart;
  const newRichTitle = (
    <span>
      {richText(newTitle2, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const showContactUser = authorAvailable && (!currentUser || (currentUser && !isOwnListing));

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  /* const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  }); */
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    lang,
  });

  const closeOrderModal = (history, location) => {
    const { pathname, search, state } = location;
    const searchParams = omit(parse(search), 'orderOpen');
    const searchString = `?${stringify(searchParams)}`;
    history.push(`${pathname}${searchString}`, state);
  };

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;

    if (isOwnListing || isCurrentlyClosed) {
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
    } else {
      setShowPriceDetails(true);

      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );

  // Most listings in the catalogue have an empty `description` attribute. Without a
  // fallback, Page falls back to the site-wide description and every listing page
  // ends up with the exact same meta description and og:description.
  const schemaCountryName = publicData?.country ? getCountryName(publicData.country) : null;
  const schemaLocation = [publicData?.city, schemaCountryName]
    .filter(part => !!part && part !== 'Country code not found')
    .join(', ');
  const seoDescription =
    description ||
    intl.formatMessage(
      {
        id: schemaLocation
          ? 'ListingPage.schemaDescriptionFallback'
          : 'ListingPage.schemaDescriptionFallbackNoLocation',
      },
      { title, location: schemaLocation, marketplaceName }
    );

  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
        price: intl.formatNumber(convertMoneyToNumber(price), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        priceCurrency: price.currency,
      }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  /*   const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label })); */
  const googleReviews = publicData?.location?.reviews ?? [];
  const googleRating = publicData?.location?.rating ?? 0;

  const hasGoogleReviews = googleReviews.length > 0 && googleRating > 0;
  const schemaReviewsMaybe = hasGoogleReviews
    ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: googleRating,
          reviewCount: googleReviews.length,
        },
        review: googleReviews.map(review => ({
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.rating,
          },
          author: {
            '@type': 'Person',
            name: review.author_name,
            url: review.author_url,
          },
          reviewBody: review.language === 'de-DE' ? review.text_de : review.text,
          datePublished: new Date(review.time * 1000).toISOString(),
        })),
      }
    : {};

  const activitiesEn =
    publicData && publicData.activities ? publicData.activities : '';
  const activitiesDe =
    publicData && publicData.activities_de ? publicData.activities_de : '';

  let ProjectRoomtype = null;

  const currentCity =
    currentListing &&
    currentListing.attributes.publicData &&
    currentListing.attributes.publicData.city
      ? currentListing.attributes.publicData.city
      : 'No city available';
  const howToGetThereEn = publicData && publicData.arrival ? publicData.arrival : [];
  const howToGetThereDe = publicData && publicData.arrival_de ? publicData.arrival_de : [];
  /*   const currentPrice =
    currentListing && currentListing.attributes ? currentListing.attributes.price.amount / 100 : [];
  const currentCurrency =
    currentListing &&
    currentListing.attributes &&
    currentListing.attributes.price.currency === 'EUR'
      ? String.fromCharCode(8364)
      : '$';
  const currentDayUnit = cookies.get('language') === 'en' ? 'per Night' : 'per Nacht'; */
  const actionBar = listingId ? (
    <div onClick={e => e.stopPropagation()} className={css.actionBarParent}>
      <ActionBarMaybe
        className={css.actionBar}
        isOwnListing={isOwnListing}
        listing={currentListing}
        editParams={{
          id: listingId.uuid,

          slug: listingSlug,

          type: listingPathParamType,

          tab: listingTab,
        }}
      />
    </div>
  ) : null;

  const fetchBookingAvailabilityData = async data => {
    if (currentUser) {
      const { listingId, orderData } = data;
      const { bookingEnd, bookingStart, seats } = orderData;
      let transitionName = 'transition/paypal-payment';
      let params = {
        bookingDates: { bookingEnd, bookingStart },
        seats,
        listingId,
        cardToken: 'CheckoutPage_speculative_card_token',
      };
      let processAlias =
        config.listing?.listingTypes[0]?.transactionType?.alias ?? 'default-booking/release-1';
      let isPrivileged = true;
      let response = await fetchSpeculatedTransaction(
        params,
        processAlias,
        null,
        transitionName,
        isPrivileged
      );
      if (response.code != 200) {
        setBookingDisabled(true);
        setShowBookingNoAvailableError(true);
      } else {
        setBookingDisabled(false);
        setShowBookingNoAvailableError(false);
      }
    } else {
      setBookingDisabled(false);
      setShowBookingNoAvailableError(false);
    }
    onFetchTransactionLineItems(data);
  };
  const displayBothReviews = true;

  const transformListingData = inputData => {
    // Extract the necessary data
    const { id, attributes, images, author } = inputData;

    // Create the slug from the title
    const slug = attributes.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    // Format the price
    const priceInEuros = attributes.price?.amount ? attributes.price.amount / 100 : 0;
    const formattedPrice = attributes.price?.amount ? `${priceInEuros}€` : 'Price not available';

    // Get the first image URL (landscape-crop2x variant)
    const imageUrl = images[0]?.attributes?.variants['landscape-crop2x']?.url || '';

    // Format the location
    const location = `${attributes.publicData.city}, ${
      attributes.publicData.country === 'EC' ? 'Ecuador' : attributes.publicData.country
    }`;

    // Get project type (using category as project type in this case)
    const projectType = ['italy'];

    // Create the transformed object
    return {
      link: `/l/${slug}/${id.uuid}`,
      linkName: 'ListingPage',
      linkParams: {
        slug: slug,
        id: id.uuid,
      },
      name: attributes.title,
      price: formattedPrice,
      location: location,
      projectType: projectType,
      image: imageUrl,
      finalReviews: attributes.publicData.finalReviews?.toString() || '0',
      totalFinalReviews: attributes.publicData.finalReviews || 0,
    };
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      noChildrenMargin
      author={authorDisplayName}
      description={seoDescription}
      openGraphType="product"
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: seoDescription,
        name: schemaTitle,
        url: productURL,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
        // Google rejects an AggregateRating whose ratingValue or reviewCount is 0,
        // and one invalid property invalidates the whole Product item. Listings with
        // no Google reviews must therefore omit the rating and review blocks entirely.
        ...schemaReviewsMaybe,
      }}
    >
      <ElbwalkerInitializer />
      <div
        data-elb={elbEntity}
        data-elbglobals={`pagetype:${isExperienceType ? 'experience' : 'accomodation'}`}
        data-elbcontext="booking:detail"
        data-elbaction={`load:view-${currentListing?.attributes?.title}-uuid-${currentListing?.id?.uuid};`}
      >
        <div className={css.pageWrapper}>
          <AuthModal
            isOpen={authModalOpen}
            setOpen={setAuthModalOpen}
            redirectToCheckout={() => {
              if (orderValues) {
                handleOrderSubmit(orderValues);
              } else if (callFav) {
                favoriteFunction();
                setCallFav(false);
              }
            }}
          />
          {notification && (
            <Notification
              message={intl.formatMessage({ id: notification })}
              onClose={() => setNotification(null)}
            />
          )}
          {topbar}
          {actionBar}
          <div className={css.contentContainer} {...elbProps(`name:${listingSlug}`)}>
            <ImageGallery listing={currentListing}>
              <ListingHeader
                currentListing={currentListing}
                isExperienceType={isExperienceType}
                currentLanguage={props.currentLanguage}
                countryName={countrynameNew}
                city={currentCity}
                isOwnListing={isOwnListing}
                isFavorite={favorite}
                onFavoriteClick={() => {
                  if (isAuthenticated) {
                    favoriteFunction();
                  } else {
                    setCallFav(true);
                    setAuthModalOpen(true);
                  }
                }}
                shareUrl={productURL}
                reviews={reviews}
                openModalFromOutside={openModalFromOutside}
              />
            </ImageGallery>
            <ImpactTagsRow />
            <main className={css.main}>
              <div
                className={css.contentCol}
                {...elbProps(`city:${currentCity};country:${countrynameNew}`)}
                data-elbaction={`wait(100):isAuthenticated:${currentUser?.attributes?.email ||
                  'false'}`}
              >
                <ImpactStrip
                  currentListing={currentListing}
                  currentLanguage={props.currentLanguage}
                  reviews={reviews}
                />
                <HostTeaser
                  currentListing={currentListing}
                  currentLanguage={props.currentLanguage}
                  reviews={reviews}
                />
                <Highlights
                  publicData={publicData}
                  currentLanguage={props.currentLanguage}
                  isExperienceType={isExperienceType}
                />
                <DescriptionSection
                  currentListing={currentListing}
                  isExperienceType={isExperienceType}
                  activitiesEn={activitiesEn}
                  activitiesDe={activitiesDe}
                  currentLanguage={props.currentLanguage}
                />
                <VideoSection currentListing={currentListing} />
                <ReviewsSection
                  currentListing={currentListing}
                  reviews={reviews}
                  googleReviews={googleReviews}
                  currentLanguage={props.currentLanguage}
                  showModalFromOutside={openModalFromOutside}
                  isModalOpen={showModalFromOutside}
                  closeModalFromOutside={() => setShowModalFromOutside(false)}
                  displayBothReviews={displayBothReviews}
                />
                <AboutHost
                  currentListing={currentListing}
                  currentLanguage={props.currentLanguage}
                  reviews={reviews}
                />
                <MapSection
                  geolocation={geolocation}
                  publicData={publicData}
                  listingId={currentListing.id}
                  howToGetThereEn={howToGetThereEn}
                  howToGetThereDe={howToGetThereDe}
                  currentLanguage={props.currentLanguage}
                />
                <OnSiteTabs publicData={publicData} />
                {isExperienceType && publicData?.FAQs?.length > 0 && (
                  <FAQAccordion
                    questions={publicData.FAQs}
                    currentLanguage={props.currentLanguage}
                  />
                )}
                {isExperienceType && <GutZuWissen embedded />}
                <GetRoomTypesCard />
              </div>
              <aside
                className={css.bookingCol}
                {...elbProps(
                  `room_type:${publicData?.roomType ? publicData?.roomType : publicData?.roomtype}`
                )}
              >
                <BookingPanel>
                  <OrderPanel
                    className={css.productOrderPanel}
                    listing={currentListing}
                    isOwnListing={isOwnListing}
                    onSubmit={handleOrderSubmit}
                    currentListingRating={currentListingRating}
                    reviewCount={listingReviewStats.count}
                  authorLink={
                    <NamedLink
                      className={css.authorNameLink}
                      name="ListingPage"
                      params={params}
                      to={{ hash: '#author' }}
                    >
                      {authorDisplayName}
                    </NamedLink>
                  }
                  title={richTitle}
                  titleDesktop={
                    <H4 as="h1" className={css.orderPanelTitle}>
                      {newRichTitle}
                    </H4>
                  }
                  author={ensuredAuthor}
                  onManageDisableScrolling={onManageDisableScrolling}
                  onContactUser={onContactUser}
                  monthlyTimeSlots={monthlyTimeSlots}
                  onFetchTimeSlots={onFetchTimeSlots}
                  onFetchTransactionLineItems={fetchBookingAvailabilityData}
                  lineItems={lineItems}
                  fetchLineItemsInProgress={fetchLineItemsInProgress}
                  fetchLineItemsError={fetchLineItemsError}
                  marketplaceCurrency={config.currency}
                  dayCountAvailableForBooking={config.dayCountAvailableForBooking}
                  marketplaceName={config.marketplaceName}
                  setAuthModalOpen={setAuthModalOpen}
                  currentUser={currentUser}
                  ProjectRoomtype={ProjectRoomtype}
                  roomTypeRaw={publicData?.roomtype}
                  bedamount={publicData?.bedamount}
                  showPriceDetails={showPriceDetails}
                  bookingDisabled={bookingDisabled}
                  showBookingNoAvailableError={showBookingNoAvailableError}
                  />
                </BookingPanel>
              </aside>
            </main>
            <ContactHelp />
          </div>
          <div className={css.listingFooterWrap}>
            <Footer />
          </div>
        </div>
      </div>
      <ListingContactModal
        isOpen={isContactModalOpen}
        onClose={handleContactModalClose}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  listingConfig: null,
  lineItems: null,
  fetchLineItemsError: null,
  user: null,
  hasListings: false,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  fetchSpeculatedTransaction: func.isRequired,
  listingConfig: object,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  hasListings: bool.isRequired,
  user: propTypes.user,
  listings: array,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    userListingRefs,
    userId,
    hasListings,
  } = state.ListingPage;
  const { currentUser } = state.user;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  let listings = [];
  if (userListingRefs) {
    listings = getMarketplaceEntities(state, userListingRefs);
  }
  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    listings,
    currentLanguage: state?.ui?.language || 'en',
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  fetchSpeculatedTransaction: (params, processAlias, txId, transitionName, isPrivileged) =>
    dispatch(speculateTransactionLP(params, processAlias, txId, transitionName, isPrivileged)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
